import { icon } from "@msuite/ui/icons";
import { useTranslation } from "@msuite/ui/localization";
import { SegmentedControl } from "@msuite/ui/segmented-control";
import { useState } from "react";
import { usePlanStore } from "@/components/plan/store";
import {
	CAPACITY_RESOURCE_CATEGORIES,
	type CapacityResourceCategory,
	type CapacityResourceCategoryValue,
} from "../capacity-resource-categories";
import { CapacityResourceList } from "./capacity-resource-list";
import { CrewDefinitionResources } from "./crew-definition-resources";
import { ManagerSlotResources } from "./manager-slot-resources";

export function CapacityResources() {
	/** Store */
	const capacityView = usePlanStore((state) => state.capacityView);

	/** Hooks */
	const { t } = useTranslation();

	/** State */
	const [resourceType, setResourceType] = useState<CapacityResourceCategoryValue>("crews");

	/** Functions */
	function getActiveCategory(): CapacityResourceCategory | null {
		for (const category of CAPACITY_RESOURCE_CATEGORIES) {
			if (category.value === resourceType) return category;
		}
		return null;
	}

	/** Computed */
	const activeCategory = getActiveCategory();
	const isPlannerView = capacityView === "planner";

	/** Render */
	// The panel is the daily board's resources panel — same width, same border, same two-row grid —
	// so the switch between the two modes keeps the right-hand column in place.
	return (
		<div
			className="grow-0 min-h-0 overflow-hidden h-full w-[500px] px-4 border-l-[0.5px] shadow-lg border-color pb-1 grid gap-y-4"
			style={{ gridTemplateRows: "max-content 1fr" }}
		>
			{/* The control keeps its row at one category, because it is the panel's own header and
			    the categories beside crews are what it is waiting for. */}
			<SegmentedControl
				value={resourceType}
				onChange={setResourceType}
				items={CAPACITY_RESOURCE_CATEGORIES.map((category) => ({
					label: t(category.labelKey),
					value: category.value,
					cyId: `capacity-resources-${category.value}`,
				}))}
			/>
			{/* The daily board drops an assignment here to undo it. Capacity planning has no
			    assignment to undo yet, so the row is a plain box until its mutators exist. */}
			<div className="grow-0 w-full h-full min-h-0 overflow-hidden">
				{/* Crews are the one category with entities behind them. The placeholder stays for
				    every other, which is what makes the difference between a defined category and a
				    named one visible rather than something to remember. */}
				{/* The two boards ask different questions of the same crews: a planner wants every
				    crew there is, a manager wants the ones they hold a window over. This is where
				    defining a crew is confined to the planner side — the manager's list is a read. */}
				{activeCategory?.value === "crews" &&
					(isPlannerView ? <CrewDefinitionResources /> : <ManagerSlotResources />)}
				{activeCategory && activeCategory.value !== "crews" && (
					<CapacityResourceList
						title={t(activeCategory.labelKey)}
						icon={icon(activeCategory.iconName)}
						searchKey={`capacity-${activeCategory.value}-resources`}
					/>
				)}
			</div>
		</div>
	);
}
